import axios, { AxiosInstance, AxiosError } from 'axios';
import type {
  GeneratePersonaRequest,
  Persona,
  PersonaGenerationJob,
  PromptTemplate,
  CreateTemplateRequest,
  UpdateTemplateRequest,
  OptimizationRun,
  CreateOptimizationRunInput,
  Workspace,
  CreateWorkspaceRequest,
  MonthlyUsageSummary,
  PaginatedResponse,
  Project,
  ProjectSummary,
  CreateProjectInput,
  UpdateProjectInput,
} from '@personawiz/shared';
import { getValidToken } from '../auth/tokenBridge';
import { requestReauth } from '../auth/ReauthDialog';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api/v1';

// Create axios instance
const api: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor — always go through getValidToken() per the SPA auth
// integration doc. The package proactively refreshes near-expiry tokens
// (default refreshBufferSeconds=300), so requests rarely see a 401.
api.interceptors.request.use(async (config) => {
  const token = await getValidToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor — on 401, try ONE silent refresh + replay. If that
// also fails, the refresh token is truly gone: prompt the user via the
// ReauthDialog (mounted at the app root). The dialog's "Sign in" button
// calls redirectToLogin(), which silently re-auths through the BrainDance
// central session when it's still valid — no cross-site logout.
//
// IMPORTANT: do NOT call the SDK's logout() here. logout() redirects through
// Cognito's /logout endpoint and wipes the SHARED central session cookie on
// auth.braindance.net, signing the user out of every BrainDance site. A
// locally-expired SPA token is almost never a reason to do that — the
// central session is usually still healthy and would have re-auth'd silently.
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && originalRequest && !originalRequest.headers['X-Retry']) {
      const refreshed = await getValidToken();
      if (refreshed) {
        originalRequest.headers['X-Retry'] = 'true';
        originalRequest.headers.Authorization = `Bearer ${refreshed}`;
        return api(originalRequest);
      }

      // Refresh failed — refresh token expired or revoked. Prompt the user
      // via the ReauthDialog; the user's click will route through the SSO
      // portal, which preserves the central session cookie (if still valid)
      // and silently re-auths.
      requestReauth();
    }

    return Promise.reject(error);
  },
);

// ── BrainDance Token API client ──────────────────────────────────────────
const BRAINDANCE_API_URL = import.meta.env.VITE_BRAINDANCE_API_URL
  || (import.meta.env.DEV ? '/braindance-api' : 'https://api.braindance.net');
const SITE_ID = (import.meta.env.VITE_SITE_ID as string | undefined) || 'PersonaFab';

const braindanceApi: AxiosInstance = axios.create({
  baseURL: BRAINDANCE_API_URL,
  headers: { 'Content-Type': 'application/json' },
});

const siteApi: AxiosInstance = axios.create({
  baseURL: '',
  headers: { 'Content-Type': 'application/json' },
});

braindanceApi.interceptors.request.use(async (config) => {
  const token = await getValidToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

siteApi.interceptors.request.use(async (config) => {
  const token = await getValidToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export interface CanonicalUsageSummary {
  user_id: string;
  period: string;
  balance: number;
  total_used: number;
  total_input_tokens: number;
  total_output_tokens: number;
  total_requests: number;
  total_purchased: number;
}

export const TOKENS_USAGE_SUMMARY_PATH = '/tokens/usage/summary';

export async function fetchCanonicalUsageSummary(
  client: Pick<AxiosInstance, 'get'>,
  period?: string,
): Promise<CanonicalUsageSummary> {
  const resolvedPeriod = period || 'current_month';

  const response = await client.get<{ success: boolean; data?: CanonicalUsageSummary; error?: string }>(
    TOKENS_USAGE_SUMMARY_PATH,
    {
      params: { period: resolvedPeriod },
      headers: { 'X-Site-Id': SITE_ID },
    },
  );

  if (!response.data.success || !response.data.data) {
    throw new Error(response.data.error || 'Invalid usage summary response contract');
  }

  return response.data.data;
}

export const braindanceTokenService = {
  async getBalance(): Promise<{
    balance: number;
    used?: number;
    limit?: number;
    periodStart?: string;
    periodEnd?: string;
    updatedAt?: string;
  }> {
    const summary = await fetchCanonicalUsageSummary(siteApi, 'current_month');

    return {
      balance: summary.balance,
      used: summary.total_used,
      periodStart: summary.period,
      periodEnd: summary.period,
      updatedAt: new Date().toISOString(),
    };
  },

  async getUsageSummary(period?: string): Promise<{
    user_id: string;
    period: string;
    balance: number;
    total_used: number;
    total_input_tokens: number;
    total_output_tokens: number;
    total_requests: number;
    total_purchased: number;
  }> {
    return fetchCanonicalUsageSummary(siteApi, period);
  },
};

// User type for frontend
export interface User {
  id: string;
  email: string;
  name: string;
  defaultWorkspaceId: string;
}

// Auth Service - Note: OTP auth uses amplify, these are for reference
export const authService = {
  async getCurrentUser() {
    const response = await api.get('/auth/me');
    return response.data;
  },
};

// Persona Service
export const personaService = {
  async generate(data: GeneratePersonaRequest): Promise<Persona> {
    const response = await api.post<{ data: PersonaGenerationJob }>('/personas/generate', data);
    const job = response.data.data;
    if (job.status === 'failed') {
      throw new Error(job.error || 'Persona generation failed');
    }
    if (!job.persona) {
      throw new Error('Persona generation completed but no persona was returned');
    }
    return job.persona;
  },

  async getById(id: string): Promise<Persona> {
    const response = await api.get<{ data: Persona }>(`/personas/${id}`);
    return response.data.data;
  },

  async list(params?: { page?: number; pageSize?: number; search?: string }): Promise<PaginatedResponse<Persona>> {
    const response = await api.get<PaginatedResponse<Persona>>('/personas', { params });
    return response.data;
  },

  async delete(id: string): Promise<void> {
    await api.delete(`/personas/${id}`);
  },
};

// Prompt Template Service
export const promptTemplateService = {
  async create(data: CreateTemplateRequest): Promise<PromptTemplate> {
    const response = await api.post<{ data: PromptTemplate }>('/templates', data);
    return response.data.data;
  },

  async getById(id: string, version?: number): Promise<PromptTemplate> {
    const params = version ? { version } : undefined;
    const response = await api.get<{ data: PromptTemplate }>(`/templates/${id}`, { params });
    return response.data.data;
  },

  async list(params?: { limit?: number; cursor?: string }): Promise<PaginatedResponse<PromptTemplate>> {
    const response = await api.get<PaginatedResponse<PromptTemplate>>('/templates', { params });
    return response.data;
  },

  async update(id: string, data: UpdateTemplateRequest): Promise<PromptTemplate> {
    const response = await api.patch<{ data: PromptTemplate }>(`/templates/${id}`, data);
    return response.data.data;
  },

  async delete(id: string): Promise<void> {
    await api.delete(`/templates/${id}`);
  },

  async getVersions(id: string): Promise<PromptTemplate[]> {
    const response = await api.get<{ data: PromptTemplate[] }>(`/templates/${id}/versions`);
    return response.data.data;
  },

  async render(id: string, variables: Record<string, string>): Promise<{ renderedContent: string }> {
    const response = await api.post<{ data: { renderedContent: string } }>(`/templates/${id}/render`, { variables });
    return response.data.data;
  },
};

// Optimization Run Service
export const optimizationRunService = {
  async create(data: CreateOptimizationRunInput): Promise<OptimizationRun> {
    const response = await api.post<{ data: OptimizationRun }>('/optimization-runs', data);
    return response.data.data;
  },

  async getById(id: string): Promise<OptimizationRun> {
    const response = await api.get<{ data: OptimizationRun }>(`/optimization-runs/${id}`);
    return response.data.data;
  },

  async list(params?: { limit?: number; cursor?: string; status?: string }): Promise<PaginatedResponse<OptimizationRun>> {
    const response = await api.get<PaginatedResponse<OptimizationRun>>('/optimization-runs', { params });
    return response.data;
  },

  async cancel(id: string): Promise<OptimizationRun> {
    const response = await api.post<{ data: OptimizationRun }>(`/optimization-runs/${id}/cancel`);
    return response.data.data;
  },
};

// Workspace Service
export const workspaceService = {
  async create(data: CreateWorkspaceRequest): Promise<Workspace> {
    const response = await api.post<{ data: Workspace }>('/workspaces', data);
    return response.data.data;
  },

  async getById(id: string): Promise<Workspace> {
    const response = await api.get<{ data: Workspace }>(`/workspaces/${id}`);
    return response.data.data;
  },

  async list(): Promise<Workspace[]> {
    const response = await api.get<{ data: Workspace[] }>('/workspaces');
    return response.data.data;
  },

  async update(id: string, data: Partial<CreateWorkspaceRequest>): Promise<Workspace> {
    const response = await api.put<{ data: Workspace }>(`/workspaces/${id}`, data);
    return response.data.data;
  },

  async delete(id: string): Promise<void> {
    await api.delete(`/workspaces/${id}`);
  },

  async inviteMember(id: string, email: string, role: string): Promise<void> {
    await api.post(`/workspaces/${id}/invitations`, { email, role });
  },

  async removeMember(workspaceId: string, userId: string): Promise<void> {
    await api.delete(`/workspaces/${workspaceId}/members/${userId}`);
  },
};

// Usage Service
export const usageService = {
  async getSummary(params?: { startDate?: string; endDate?: string }): Promise<MonthlyUsageSummary> {
    const response = await api.get<{ data: MonthlyUsageSummary }>('/usage/summary', { params });
    return response.data.data;
  },

  async getDetails(params?: { startDate?: string; endDate?: string; limit?: number; cursor?: string }) {
    const response = await api.get('/usage/details', { params });
    return response.data;
  },
};

// Project Service
export const projectService = {
  async create(data: CreateProjectInput): Promise<Project> {
    const response = await api.post<{ data: Project }>('/projects', data);
    return response.data.data;
  },

  async getById(id: string): Promise<Project> {
    const response = await api.get<{ data: Project }>(`/projects/${id}`);
    return response.data.data;
  },

  async list(params?: { page?: number; pageSize?: number; status?: string; search?: string }): Promise<PaginatedResponse<ProjectSummary>> {
    const response = await api.get<PaginatedResponse<ProjectSummary>>('/projects', { params });
    return response.data;
  },

  async update(id: string, data: UpdateProjectInput): Promise<Project> {
    const response = await api.put<{ data: Project }>(`/projects/${id}`, data);
    return response.data.data;
  },

  async delete(id: string): Promise<void> {
    await api.delete(`/projects/${id}`);
  },

  async listPersonas(projectId: string, params?: { page?: number; pageSize?: number }): Promise<PaginatedResponse<Persona>> {
    const response = await api.get<PaginatedResponse<Persona>>(`/projects/${projectId}/personas`, { params });
    return response.data;
  },

  async addPersona(projectId: string, personaId: string): Promise<void> {
    await api.post(`/projects/${projectId}/personas`, { personaId });
  },

  async removePersona(projectId: string, personaId: string): Promise<void> {
    await api.delete(`/projects/${projectId}/personas/${personaId}`);
  },
};

// Health Service
export const healthService = {
  async check() {
    const response = await api.get('/health');
    return response.data;
  },
};

export default api;
